// Holiday bread box builder (sections/bread-box-builder.liquid).
// Counts are kept on the page; "Add to cart" sends every picked bread as its own cart line.
// The saving shown here is only an estimate of the store's automatic discount, which applies at checkout.

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const formatCents = (cents) => money.format(cents / 100);
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// "2026-11-25" -> "Wednesday (11/25/2026)", the format pickup-date.js saves. Done in UTC so the
// customer's time zone can't shift the day.
function pickupProperty(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return `${WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} (${m}/${d}/${y})`;
}

class BreadBox extends HTMLElement {
  connectedCallback() {
    this.minItems = parseInt(this.dataset.minItems, 10) || 0;
    this.percent = parseInt(this.dataset.percent, 10) || 0;
    this.rows = [...this.querySelectorAll("[data-variant-id]")];
    this.counts = new Map();
    this.addButton = this.querySelector("[data-add]");
    this.error = this.querySelector("[data-error]");

    this.addEventListener("click", (event) => {
      const step = event.target.closest("[data-step]");
      if (step) this.change(step.closest("[data-variant-id]"), parseInt(step.dataset.step, 10));
    });
    this.addButton.addEventListener("click", () => this.addToCart());

    const closesAt = Date.parse(this.dataset.closesAt);
    if (closesAt && Date.now() >= closesAt) this.close();
    this.render();
  }

  close() {
    this.closed = true;
    this.querySelector("[data-closed]").hidden = false;
    this.querySelector(".bread-box__grid").hidden = true;
    this.querySelector("[data-bar]").hidden = true;
  }

  change(row, delta) {
    const id = row.dataset.variantId;
    const next = Math.max(0, (this.counts.get(id) || 0) + delta);
    if (next) this.counts.set(id, next);
    else this.counts.delete(id);
    this.error.hidden = true;
    this.render();
  }

  render() {
    let items = 0;
    let subtotal = 0;
    let discounted = 0;
    const saving = (count) => (this.percent && this.minItems && count >= this.minItems ? this.percent : 0);
    for (const row of this.rows) items += this.counts.get(row.dataset.variantId) || 0;

    for (const row of this.rows) {
      const qty = this.counts.get(row.dataset.variantId) || 0;
      const line = qty * parseInt(row.dataset.price, 10);
      subtotal += line;
      discounted += line - Math.round((line * saving(items)) / 100);
      const qtyLabel = row.querySelector("[data-qty]");
      if (qtyLabel) qtyLabel.textContent = qty;
      const minus = row.querySelector('[data-step="-1"]');
      if (minus) minus.disabled = qty === 0;
    }
    for (const card of this.querySelectorAll(".bread-box__card")) {
      card.classList.toggle("is-picked", [...card.querySelectorAll("[data-variant-id]")].some((r) => this.counts.has(r.dataset.variantId)));
    }

    const progress = this.querySelector("[data-progress]");
    const meter = this.querySelector("[data-meter]");
    const breads = (n) => `${n} ${n === 1 ? "bread" : "breads"}`;
    if (!this.minItems || !this.percent) {
      progress.textContent = items ? `${breads(items)} in your box` : "Tap + to add breads to your box";
      meter.parentElement.hidden = true;
    } else if (items >= this.minItems) {
      progress.textContent = `${breads(items)}: you're saving ${this.percent}%`;
    } else if (items === 0) {
      progress.textContent = `Pick any ${this.minItems} breads to save ${this.percent}%`;
    } else {
      progress.textContent = `${breads(items)}: add ${this.minItems - items} more to save ${this.percent}%`;
    }
    if (this.minItems) meter.style.width = `${Math.min(100, (items / this.minItems) * 100)}%`;

    const was = this.querySelector("[data-was]");
    was.hidden = discounted === subtotal;
    was.textContent = formatCents(subtotal);
    this.querySelector("[data-total]").textContent = formatCents(discounted);
    this.addButton.disabled = items === 0 || this.closed;
    this.addButton.textContent = items ? `Add ${items} to cart` : "Add to cart";
  }

  async addToCart() {
    const items = this.rows
      .filter((row) => this.counts.has(row.dataset.variantId))
      .map((row) => ({
        id: parseInt(row.dataset.variantId, 10),
        quantity: this.counts.get(row.dataset.variantId),
        ...(row.dataset.pickupDate ? { properties: { "Pickup Date": pickupProperty(row.dataset.pickupDate) } } : {}),
      }));
    if (!items.length) return;

    this.addButton.disabled = true;
    this.addButton.textContent = "Adding…";
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || "Something went wrong. Please try again.");
      }
      window.location.href = `${window.Shopify?.routes?.root || "/"}cart`;
    } catch (err) {
      this.error.textContent = err.message;
      this.error.hidden = false;
      this.render();
    }
  }
}

if (!customElements.get("bread-box")) customElements.define("bread-box", BreadBox);
